import { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Input,
  Skeleton,
  Drawer,
  Modal,
  ErrorBoundary,
  Timeline,
  useToast,
} from '@bbpos/ui';
import type { TimelineItem } from '@bbpos/ui';
import { GLASS, GLASS_FORM } from '@/constants/design-tokens';
import {
  useStockTransfer,
  useDispatchTransfer,
  useConfirmTransfer,
  useCancelTransfer,
  useRejectRemainingTransfer,
  TransferStatusBadge,
  StKpi,
} from '@/features/stock-transfers';
import type { TransferItem } from '@/features/stock-transfers';
import { useAuthStore } from '@/stores/use-auth-store';
import { ROLE_LEVELS } from '@/types/user';
import {
  ArrowRightIcon,
  ArrowsRightLeftIcon,
  CheckIcon,
  BanIcon,
  PackageIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  LockIcon,
  TruckIcon,
} from './icons';

// ─── Status rail config — left colored bar (Glass Depth + status signal) ────

const STATUS_RAIL_BASE =
  "relative pl-5 before:content-[''] before:absolute before:left-0 before:top-3 before:bottom-3 before:w-1 before:rounded-full";

const STATUS_RAIL: Record<string, { rail: string; halo: string }> = {
  PENDING:    { rail: 'before:bg-gradient-to-b before:from-warning-400 before:to-warning-600', halo: 'bg-warning-100/30 dark:bg-warning-900/30' },
  IN_TRANSIT: { rail: 'before:bg-gradient-to-b before:from-brand-400 before:to-brand-600',     halo: 'bg-brand-100/30 dark:bg-brand-900/30'     },
  PARTIALLY_RECEIVED: { rail: 'before:bg-gradient-to-b before:from-brand-400 before:to-warning-500', halo: 'bg-brand-100/30 dark:bg-brand-900/30' },
  COMPLETED:  { rail: 'before:bg-gradient-to-b before:from-success-400 before:to-success-600', halo: 'bg-success-100/30 dark:bg-success-900/30' },
  PARTIALLY_ACCEPTED: { rail: 'before:bg-gradient-to-b before:from-warning-400 before:to-warning-600', halo: 'bg-warning-100/30 dark:bg-warning-900/30' },
  RECEIVED:   { rail: 'before:bg-gradient-to-b before:from-success-400 before:to-success-600', halo: 'bg-success-100/30 dark:bg-success-900/30' },
  REJECTED:   { rail: 'before:bg-gradient-to-b before:from-danger-400 before:to-danger-600',   halo: 'bg-danger-100/30 dark:bg-danger-900/30'   },
  CANCELLED:  { rail: 'before:bg-gradient-to-b before:from-danger-400 before:to-danger-600',   halo: 'bg-danger-100/30 dark:bg-danger-900/30'   },
};

// PENDING is phrased with the source name at render time — stock only leaves the
// source once it dispatches (three-step flow: requested → in transit → received).
const STATUS_DESCRIPTIONS: Record<string, string> = {
  PENDING:    'Requested — awaiting dispatch from the source location. No stock has moved yet.',
  IN_TRANSIT: 'Goods are in transit between locations. Awaiting receipt confirmation.',
  PARTIALLY_RECEIVED: 'Part of the stock has been received. The rest is still in transit — receive it when it arrives, or reject it.',
  COMPLETED:  'Transfer complete. Stock has been updated at both locations.',
  PARTIALLY_ACCEPTED: 'Closed with stock not received. The rejected stock was written off at the source as damaged — it was not added back.',
  RECEIVED:   'Goods have been received at the destination location.',
  REJECTED:   'This transfer was rejected.',
  CANCELLED:  'This transfer was cancelled. Any dispatched stock has been returned to the source.',
};

// ─── Confirm-receipt drawer state ───────────────────────────────────────────

interface AcceptedItemState {
  /** product_id (retail) or ingredient_id (restaurant) — whichever the line carries. */
  ref_id: string;
  item_type: 'PRODUCT' | 'INGREDIENT';
  product_name: string;
  product_sku: string | null;
  unit: string | null;
  /** Still in transit on this line: sent − received so far − rejected. */
  remaining_quantity: number;
  accepted_quantity: string;
}

/** Quantities are 3-decimal: subtract in thousandths so 2.25 − 2 shows 0.25. */
function inTransitQty(item: TransferItem): number {
  const milli = (n: number | null | undefined) => Math.round(Number(n ?? 0) * 1000);
  return (milli(item.quantity) - milli(item.accepted_quantity) - milli(item.rejected_quantity)) / 1000;
}

// ─── Variance badge for line items ──────────────────────────────────────────

export function VarianceBadge({ sent, accepted }: { sent: number; accepted: number }) {
  if (accepted >= sent) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-success-50 dark:bg-success-900/30 text-success-700 dark:text-success-300 px-2 py-0.5 text-[10px] font-semibold tabular-nums font-mono">
        <CheckIcon className="h-2.5 w-2.5" />
        Full
      </span>
    );
  }
  const pct = sent > 0 ? Math.round(((sent - accepted) / sent) * 100) : 0;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-warning-50 dark:bg-warning-900/30 text-warning-800 dark:text-warning-300 px-2 py-0.5 text-[10px] font-semibold tabular-nums font-mono">
      <AlertTriangleIcon className="h-2.5 w-2.5" />
      {pct}% short
    </span>
  );
}

// ─── Detail Content ─────────────────────────────────────────────────────────

function StockTransferDetailContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const profile = useAuthStore((s) => s.profile);
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const isManager = roleLevel >= ROLE_LEVELS.MANAGER;

  const [showReceiptDrawer, setShowReceiptDrawer] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [acceptedItems, setAcceptedItems] = useState<AcceptedItemState[]>([]);
  const [confirmNotes, setConfirmNotes] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const { data: transfer, isLoading, error } = useStockTransfer(id);
  const dispatchMutation = useDispatchTransfer();
  const confirmMutation = useConfirmTransfer();
  const cancelMutation = useCancelTransfer();
  const rejectMutation = useRejectRemainingTransfer();

  const items = useMemo(() => transfer?.items ?? [], [transfer]);
  // Dispatch belongs to the source; Receive to the destination. Receiving a
  // PENDING transfer dispatches it implicitly for owners (the API answers 409
  // for a destination-only manager — the toast explains).
  const canDispatch = !!transfer && transfer.status === 'PENDING' && isManager;
  // A part-received transfer stays open: receive the rest later or reject it (IS2-001).
  const isPartReceived = transfer?.status === 'PARTIALLY_RECEIVED';
  const canConfirm = !!transfer && (transfer.status === 'PENDING' || transfer.status === 'IN_TRANSIT' || isPartReceived) && isManager;
  const canReject = !!transfer && (transfer.status === 'IN_TRANSIT' || isPartReceived) && isManager;
  const canCancel = !!transfer && (transfer.status === 'PENDING' || transfer.status === 'IN_TRANSIT') && isManager;
  const isCompleted = transfer?.status === 'COMPLETED' || transfer?.status === 'RECEIVED' || transfer?.status === 'PARTIALLY_ACCEPTED';
  const showReceived = isCompleted || isPartReceived;

  const openReceiptDrawer = useCallback(() => {
    if (!transfer) return;
    // Only lines with stock still in transit — earlier receipts already took the rest in.
    setAcceptedItems(
      transfer.items
        .filter((item) => inTransitQty(item) > 0)
        .map((item) => ({
          ref_id: (item.item_type === 'INGREDIENT' ? item.ingredient_id : item.product_id) ?? '',
          item_type: item.item_type === 'INGREDIENT' ? 'INGREDIENT' : 'PRODUCT',
          product_name: item.product_name,
          product_sku: item.product_sku ?? null,
          unit: item.unit ?? null,
          remaining_quantity: inTransitQty(item),
          accepted_quantity: String(inTransitQty(item)),
        })),
    );
    setConfirmNotes('');
    setShowReceiptDrawer(true);
  }, [transfer]);

  const handleDispatch = useCallback(() => {
    if (!id || !transfer) return;
    dispatchMutation.mutate(
      { id },
      {
        onSuccess: () => toast({ title: `Transfer ${transfer.transfer_number} dispatched — goods in transit`, variant: 'success' }),
        onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
      },
    );
  }, [id, transfer, dispatchMutation, toast]);

  // Keyboard shortcuts: d = dispatch, r = receive, x = cancel, Esc handled by overlays
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'd' && canDispatch && !showReceiptDrawer && !showCancelDialog && !dispatchMutation.isPending) {
        e.preventDefault();
        handleDispatch();
      }
      if (e.key === 'r' && canConfirm && !showReceiptDrawer && !showCancelDialog) {
        e.preventDefault();
        openReceiptDrawer();
      }
      if (e.key === 'x' && canCancel && !showReceiptDrawer && !showCancelDialog) {
        e.preventDefault();
        setShowCancelDialog(true);
      }
      if (e.key === 'Escape' && !showReceiptDrawer && !showCancelDialog && !showRejectDialog) {
        e.preventDefault();
        navigate('/inventory/stock-transfers');
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [canDispatch, canConfirm, canCancel, showReceiptDrawer, showCancelDialog, showRejectDialog, openReceiptDrawer, handleDispatch, dispatchMutation.isPending, navigate]);

  if (error) {
    toast({ title: (error as Error).message, variant: 'error' });
    navigate('/inventory/stock-transfers');
    return null;
  }

  if (isLoading || !transfer) {
    return (
      <>
        <Skeleton variant="rect" height={120} className="rounded-2xl mb-6" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={110} className="rounded-2xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <Skeleton variant="rect" height={220} className="rounded-2xl" />
            <Skeleton variant="rect" height={300} className="rounded-2xl" />
            <Skeleton variant="rect" height={200} className="rounded-2xl" />
          </div>
          <div className="space-y-4">
            <Skeleton variant="rect" height={160} className="rounded-2xl" />
            <Skeleton variant="rect" height={200} className="rounded-2xl" />
          </div>
        </div>
      </>
    );
  }

  const statusRail = STATUS_RAIL[transfer.status] ?? STATUS_RAIL.PENDING;

  // KPI summary metrics
  const totalQtySent = items.reduce((s, it) => s + Number(it.quantity ?? 0), 0);
  const totalQtyAccepted = items.reduce((s, it) => s + Number(it.accepted_quantity ?? 0), 0);
  const partialCount = items.filter((it) => it.accepted_quantity != null && it.accepted_quantity < it.quantity).length;
  const totalInTransit = items.reduce((s, it) => s + Math.max(0, inTransitQty(it)), 0);
  const variancePct =
    isCompleted && totalQtySent > 0
      ? Math.round(((totalQtySent - totalQtyAccepted) / totalQtySent) * 100)
      : 0;

  function handleConfirm() {
    if (!id) return;
    // Each receipt adds to what earlier receipts took in; what is not received
    // now stays in transit (IS2-001) — no reason needed.
    const payload = {
      accepted_items: acceptedItems.map((ai) => ({
        ...(ai.item_type === 'INGREDIENT' ? { ingredient_id: ai.ref_id } : { product_id: ai.ref_id }),
        accepted_quantity: parseFloat(ai.accepted_quantity) || 0,
      })),
      notes: confirmNotes.trim() || undefined,
    };

    confirmMutation.mutate(
      { id, payload },
      {
        onSuccess: () => {
          toast({
            title: drawerHasShortReceipt
              ? `Transfer ${transfer!.transfer_number} part received — the rest is still in transit`
              : `Transfer ${transfer!.transfer_number} confirmed`,
            variant: 'success',
          });
          setShowReceiptDrawer(false);
        },
        onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
      },
    );
  }

  function handleRejectRemaining() {
    if (!id || !rejectReason.trim()) return;
    rejectMutation.mutate(
      { id, payload: { reason: rejectReason.trim() } },
      {
        onSuccess: () => {
          toast({ title: `Transfer ${transfer!.transfer_number} closed — remaining stock written off`, variant: 'success' });
          setShowRejectDialog(false);
          setRejectReason('');
        },
        onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
      },
    );
  }

  function handleCancel() {
    if (!id) return;
    cancelMutation.mutate(
      { id, payload: { reason: cancelReason.trim() || undefined } },
      {
        onSuccess: () => {
          toast({ title: `Transfer ${transfer!.transfer_number} cancelled`, variant: 'success' });
          setShowCancelDialog(false);
          setCancelReason('');
        },
        onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
      },
    );
  }

  // Drawer cumulative metrics
  const drawerSent = acceptedItems.reduce((s, ai) => s + ai.remaining_quantity, 0);
  const drawerAccepted = acceptedItems.reduce(
    (s, ai) => s + (parseFloat(ai.accepted_quantity) || 0),
    0,
  );
  const drawerAcceptPct = drawerSent > 0 ? Math.round((drawerAccepted / drawerSent) * 100) : 0;
  const drawerHasShortReceipt = acceptedItems.some(
    (ai) => (parseFloat(ai.accepted_quantity) || 0) < ai.remaining_quantity,
  );
  const drawerOverAccepts = acceptedItems.some(
    (ai) => (parseFloat(ai.accepted_quantity) || 0) > ai.remaining_quantity,
  );

  // Build timeline from available timestamps
  const timelineItems: TimelineItem[] = [];
  timelineItems.push({
    id: 'created',
    type: 'pending',
    action: 'Transfer created',
    description: transfer.created_by_name ? `by ${transfer.created_by_name}` : undefined,
    timestamp: transfer.created_at,
  });
  if (transfer.dispatched_at || transfer.status === 'IN_TRANSIT') {
    timelineItems.push({
      id: 'in-transit',
      type: 'inTransit',
      action: transfer.status === 'IN_TRANSIT' ? 'Dispatched — goods in transit' : 'Dispatched from source',
      description: [
        transfer.dispatched_by_name ? `by ${transfer.dispatched_by_name}` : null,
        transfer.status === 'IN_TRANSIT' ? 'Awaiting receipt confirmation' : null,
      ].filter(Boolean).join(' · ') || undefined,
      timestamp: transfer.dispatched_at ?? transfer.updated_at,
    });
  }
  if (transfer.confirmed_at && showReceived) {
    timelineItems.push({
      id: 'confirmed',
      type: isPartReceived ? 'inTransit' : 'transferCompleted',
      action: isPartReceived
        ? 'Received in part — the rest is still in transit'
        : partialCount > 0
          ? `Received with ${partialCount} short-receipt line${partialCount === 1 ? '' : 's'}`
          : 'Received in full — stock updated',
      description: transfer.confirmed_by_name ? `by ${transfer.confirmed_by_name}` : undefined,
      timestamp: transfer.confirmed_at,
    });
  }
  if (transfer.status === 'PARTIALLY_ACCEPTED') {
    timelineItems.push({
      id: 'rejected-remaining',
      type: 'rejected',
      action: 'Remaining stock rejected — written off at the source',
      timestamp: transfer.updated_at,
    });
  }
  if (transfer.status === 'CANCELLED') {
    timelineItems.push({
      id: 'cancelled',
      type: 'cancelled',
      action: transfer.dispatched_at ? 'Transfer cancelled — stock returned to source' : 'Transfer cancelled before dispatch',
      timestamp: transfer.updated_at,
    });
  }
  if (transfer.status === 'REJECTED') {
    timelineItems.push({
      id: 'rejected',
      type: 'rejected',
      action: 'Transfer rejected',
      timestamp: transfer.updated_at,
    });
  }

  return (
    <>
      {/* Gradient header — status rail wraps the whole card */}
      <div className={`${STATUS_RAIL_BASE} ${statusRail.rail} mb-6 animate-fade-in`}>
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-teal-600 via-teal-500 to-brand-600 p-6 text-white shadow-[0_24px_60px_-24px_rgba(13,148,136,0.55)]">
          <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
          <div className="relative flex items-start justify-between gap-6">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="font-mono text-2xl font-bold tracking-tight text-white">{transfer.transfer_number}</h1>
                <TransferStatusBadge status={transfer.status} size="md" />
              </div>
              <p className="text-teal-100 mt-1.5 flex items-center gap-2 flex-wrap">
                <span className="font-medium">{transfer.source_location_name}</span>
                <ArrowRightIcon className="h-4 w-4 opacity-80" />
                <span className="font-medium">{transfer.destination_location_name}</span>
                <span className="text-teal-200/70">·</span>
                <span className="font-mono tabular-nums">
                  {new Date(transfer.created_at).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })}
                </span>
              </p>
            </div>
            <div className="text-right shrink-0">
              <p className="font-mono text-3xl font-bold tabular-nums">{items.length}</p>
              <p className="text-xs uppercase tracking-wider text-teal-200 mt-0.5">Items</p>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Summary Band */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StKpi
          title="Lines"
          value={items.length.toLocaleString('en-IN')}
          tone="teal"
          icon={<ArrowsRightLeftIcon className="h-5 w-5" />}
          delay={0}
        />
        <StKpi
          title="Qty Sent"
          value={totalQtySent.toLocaleString('en-IN')}
          tone="brand"
          icon={<PackageIcon className="h-5 w-5" />}
          delay={60}
        />
        <StKpi
          title="Qty Accepted"
          value={showReceived ? totalQtyAccepted.toLocaleString('en-IN') : '\u2014'}
          tone="success"
          icon={<CheckCircleIcon className="h-5 w-5" />}
          masked={!showReceived}
          delay={120}
        />
        {isPartReceived ? (
          <StKpi
            title="Still In Transit"
            value={totalInTransit.toLocaleString('en-IN')}
            tone="brand"
            icon={<TruckIcon className="h-5 w-5" />}
            subtext="Receive later or reject"
            subtextTone="warning"
            delay={180}
          />
        ) : (
          <StKpi
            title="Variance"
            value={isCompleted ? `${variancePct}%` : '\u2014'}
            tone={variancePct === 0 ? 'success' : variancePct < 10 ? 'warning' : 'danger'}
            icon={<AlertTriangleIcon className="h-5 w-5" />}
            masked={!isCompleted}
            subtext={isCompleted && partialCount > 0 ? `${partialCount} short-receipt line${partialCount === 1 ? '' : 's'}` : undefined}
            subtextTone={partialCount > 0 ? 'warning' : 'neutral'}
            delay={180}
          />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-slide-up">
        {/* Left: Details + Items + Timeline */}
        <div className="lg:col-span-2 space-y-6">
          {/* Transfer Information */}
          <div className={`${GLASS} p-6`}>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-4">
              Transfer Information
            </h3>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <dt className="text-[11px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Transfer Number</dt>
                <dd className="text-sm text-neutral-800 dark:text-neutral-200 tabular-nums font-mono">{transfer.transfer_number}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Date Created</dt>
                <dd className="text-sm text-neutral-800 dark:text-neutral-200 tabular-nums font-mono">
                  {new Date(transfer.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Source Location</dt>
                <dd className="text-sm text-neutral-800 dark:text-neutral-200 font-medium">{transfer.source_location_name}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Destination Location</dt>
                <dd className="text-sm text-neutral-800 dark:text-neutral-200 font-medium">{transfer.destination_location_name}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Created By</dt>
                <dd className="text-sm text-neutral-800 dark:text-neutral-200">{transfer.created_by_name ?? '\u2014'}</dd>
              </div>
              {transfer.dispatched_by_name && (
                <div>
                  <dt className="text-[11px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Dispatched By</dt>
                  <dd className="text-sm text-neutral-800 dark:text-neutral-200">{transfer.dispatched_by_name}</dd>
                </div>
              )}
              {transfer.dispatched_at && (
                <div>
                  <dt className="text-[11px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Dispatched At</dt>
                  <dd className="text-sm text-neutral-800 dark:text-neutral-200 tabular-nums font-mono">
                    {new Date(transfer.dispatched_at).toLocaleDateString('en-IN', {
                      day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
                    })}
                  </dd>
                </div>
              )}
              {transfer.confirmed_by_name && (
                <div>
                  <dt className="text-[11px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Confirmed By</dt>
                  <dd className="text-sm text-neutral-800 dark:text-neutral-200">{transfer.confirmed_by_name}</dd>
                </div>
              )}
              {transfer.confirmed_at && (
                <div>
                  <dt className="text-[11px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Confirmed At</dt>
                  <dd className="text-sm text-neutral-800 dark:text-neutral-200 tabular-nums font-mono">
                    {new Date(transfer.confirmed_at).toLocaleDateString('en-IN', {
                      day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
                    })}
                  </dd>
                </div>
              )}
              {transfer.notes && (
                <div className="sm:col-span-2">
                  <dt className="text-[11px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Notes</dt>
                  <dd className="text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-line">{transfer.notes}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Items Table */}
          <div className={`${GLASS} p-6`}>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-4">
              Transfer Items ({items.length})
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-100 dark:border-neutral-800">
                    <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Product</th>
                    <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">SKU</th>
                    <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Qty Sent</th>
                    {showReceived && (
                      <>
                        <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Accepted</th>
                        <th className="text-right py-2 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">{isPartReceived ? 'In Transit' : 'Variance'}</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item: TransferItem) => {
                    const accepted = Number(item.accepted_quantity ?? 0);
                    const isPartial = showReceived && item.accepted_quantity != null && accepted < item.quantity;
                    const lineInTransit = inTransitQty(item);
                    return (
                      <tr
                        key={item.id ?? item.product_id ?? item.ingredient_id ?? item.product_name}
                        className={`border-b border-neutral-50 dark:border-neutral-800/50 last:border-0 even:bg-neutral-50/40 dark:even:bg-neutral-800/20 hover:bg-brand-50/40 dark:hover:bg-brand-900/15 transition-colors ${
                          isPartial ? 'bg-warning-50/40 dark:bg-warning-900/10' : ''
                        }`}
                      >
                        <td className="py-2.5 pr-3 text-neutral-800 dark:text-neutral-200 font-medium">
                          {item.product_name}
                        </td>
                        <td className="py-2.5 pr-3 text-neutral-500 dark:text-neutral-400 font-mono text-xs tabular-nums">
                          {item.item_type === 'INGREDIENT'
                            ? <span className="inline-flex rounded-full bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 px-2 py-0.5 text-[10px] font-semibold">Ingredient</span>
                            : item.product_sku}
                        </td>
                        <td className="py-2.5 pr-3 text-right text-neutral-800 dark:text-neutral-200 tabular-nums font-mono">
                          {item.quantity}{item.unit ? <span className="text-xs text-neutral-400 ml-1">{item.unit}</span> : null}
                        </td>
                        {showReceived && (
                          <>
                            <td className={`py-2.5 pr-3 text-right tabular-nums font-mono font-medium ${
                              isPartial
                                ? 'text-warning-700 dark:text-warning-400'
                                : 'text-success-700 dark:text-success-400'
                            }`}>
                              {item.accepted_quantity ?? '\u2014'}
                            </td>
                            <td className="py-2.5 text-right">
                              {isPartReceived ? (
                                lineInTransit > 0 ? (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 px-2 py-0.5 text-[10px] font-semibold tabular-nums font-mono">
                                    <TruckIcon className="h-2.5 w-2.5" />
                                    {lineInTransit}
                                  </span>
                                ) : (
                                  <VarianceBadge sent={item.quantity} accepted={accepted} />
                                )
                              ) : item.accepted_quantity != null && (
                                <VarianceBadge sent={item.quantity} accepted={accepted} />
                              )}
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Totals row */}
            <div className="mt-4 pt-4 border-t border-neutral-100 dark:border-neutral-800">
              <div className="flex justify-between text-sm font-bold">
                <span className="text-neutral-900 dark:text-white">Total Lines</span>
                <span className="tabular-nums font-mono text-neutral-900 dark:text-white">{items.length}</span>
              </div>
              <div className="flex justify-between text-sm font-bold mt-1">
                <span className="text-neutral-900 dark:text-white">Total Qty Sent</span>
                <span className="tabular-nums font-mono text-neutral-900 dark:text-white">{totalQtySent}</span>
              </div>
              {showReceived && (
                <div className="flex justify-between text-sm font-bold mt-1">
                  <span className="text-neutral-900 dark:text-white">Total Qty Accepted</span>
                  <span className="tabular-nums font-mono text-success-700 dark:text-success-400">{totalQtyAccepted}</span>
                </div>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className={`${GLASS} p-6`}>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-4">
              Activity
            </h3>
            <Timeline items={timelineItems} />
          </div>
        </div>

        {/* Right: Status + Actions + Route */}
        <div className="space-y-4">
          {/* Status Card with rail + halo */}
          <div className={`${GLASS_FORM} ${STATUS_RAIL_BASE} ${statusRail.rail} p-5`}>
            <div className={`absolute inset-0 -z-10 rounded-2xl ${statusRail.halo}`} aria-hidden="true" />
            <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">
              Status
            </h3>
            <TransferStatusBadge status={transfer.status} size="md" />
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-3 leading-relaxed">
              {transfer.status === 'PENDING'
                ? `Requested — awaiting dispatch from ${transfer.source_location_name}. No stock has moved yet.`
                : STATUS_DESCRIPTIONS[transfer.status]}
            </p>
          </div>

          {/* Actions Card */}
          {(canDispatch || canConfirm || canReject || canCancel) && (
            <div className={`${GLASS_FORM} p-5`}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">
                Actions
              </h3>
              <div className="space-y-2">
                {canDispatch && (
                  <Button
                    variant="primary"
                    className="w-full"
                    onClick={handleDispatch}
                    disabled={dispatchMutation.isPending}
                    aria-describedby="dispatch-hint"
                  >
                    <TruckIcon className="h-4 w-4 mr-1.5" />
                    {dispatchMutation.isPending ? 'Dispatching...' : 'Dispatch from Source'}
                  </Button>
                )}
                {canConfirm && (
                  <Button
                    variant={canDispatch ? 'secondary' : 'primary'}
                    className="w-full"
                    onClick={openReceiptDrawer}
                    aria-describedby="receive-hint"
                  >
                    <CheckIcon className="h-4 w-4 mr-1.5" />
                    Confirm Receipt
                  </Button>
                )}
                {canReject && (
                  <Button
                    variant="secondary"
                    className="w-full"
                    onClick={() => setShowRejectDialog(true)}
                    aria-describedby="reject-hint"
                  >
                    <AlertTriangleIcon className="h-4 w-4 mr-1.5" />
                    Reject Remaining
                  </Button>
                )}
                {canCancel && (
                  <Button
                    variant="danger"
                    className="w-full"
                    onClick={() => setShowCancelDialog(true)}
                    aria-describedby="cancel-hint"
                  >
                    <BanIcon className="h-4 w-4 mr-1.5" />
                    Cancel Transfer
                  </Button>
                )}
              </div>
              <p id="dispatch-hint" className="sr-only">
                Dispatches the goods from the source location — stock leaves the source and the transfer becomes in transit. Press D for shortcut.
              </p>
              <p id="receive-hint" className="sr-only">
                Confirms goods received at destination and updates stock. Press R for shortcut.
              </p>
              <p id="reject-hint" className="sr-only">
                Rejects the stock still in transit: it is written off at the source and the transfer closes.
              </p>
              <p id="cancel-hint" className="sr-only">
                Cancels the transfer and returns stock to source. Press X for shortcut.
              </p>
              <p className="text-[11px] text-neutral-400 dark:text-neutral-500 mt-3 text-center font-mono">
                {canDispatch && (
                  <>
                    <kbd className="px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 mr-1">D</kbd> dispatch
                    <span className="mx-1.5">·</span>
                  </>
                )}
                <kbd className="px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 mr-1">R</kbd> receive
                <span className="mx-1.5">·</span>
                <kbd className="px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 mr-1">X</kbd> cancel
              </p>
            </div>
          )}

          {/* Locked notice for non-managers on actionable transfers */}
          {!isManager && (transfer.status === 'PENDING' || transfer.status === 'IN_TRANSIT' || isPartReceived) && (
            <div className={`${GLASS_FORM} p-5`}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-neutral-500 dark:text-neutral-400 shrink-0">
                  <LockIcon className="h-4 w-4" />
                </div>
                <div className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed">
                  Receiving and cancelling stock transfers requires <span className="font-semibold text-neutral-700 dark:text-neutral-300">Manager</span> access or above.
                </div>
              </div>
            </div>
          )}

          {/* Route visualization */}
          <div className={`${GLASS_FORM} p-5`}>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">
              Transfer Route
            </h3>
            <div className="flex flex-col items-center gap-2">
              <div className="w-full px-3 py-2.5 rounded-xl bg-teal-50/70 dark:bg-teal-900/20 text-center">
                <p className="text-[10px] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-0.5">From</p>
                <p className="text-sm font-medium text-neutral-800 dark:text-neutral-200">{transfer.source_location_name}</p>
              </div>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-100 to-brand-100 dark:from-teal-900/40 dark:to-brand-900/40 flex items-center justify-center rotate-90 shadow-sm">
                <ArrowRightIcon className="h-4 w-4 text-teal-600 dark:text-teal-400" />
              </div>
              <div className="w-full px-3 py-2.5 rounded-xl bg-brand-50/70 dark:bg-brand-900/20 text-center">
                <p className="text-[10px] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-0.5">To</p>
                <p className="text-sm font-medium text-neutral-800 dark:text-neutral-200">{transfer.destination_location_name}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm Receipt Drawer (right, wide) */}
      <Drawer
        open={showReceiptDrawer}
        onClose={() => setShowReceiptDrawer(false)}
        position="right"
        width="640px"
        title={`Confirm Receipt — ${transfer.transfer_number}`}
      >
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto px-1 py-4 space-y-4">
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Enter the quantity that arrived for each item. Anything not received stays in transit — receive it later, or reject it.
            </p>

            {/* Cumulative summary */}
            <div className={`${GLASS_FORM} p-4`}>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-400">Cumulative acceptance</div>
                <div className="flex items-center gap-3 text-sm">
                  <span className="font-mono tabular-nums text-neutral-700 dark:text-neutral-300">
                    {drawerAccepted} / {drawerSent}
                  </span>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold tabular-nums font-mono ${
                      drawerAcceptPct === 100
                        ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-300'
                        : drawerAcceptPct >= 80
                          ? 'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300'
                          : 'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-300'
                    }`}
                  >
                    {drawerAcceptPct}%
                  </span>
                </div>
              </div>
              <div className="mt-2 h-2 rounded-full bg-neutral-200 dark:bg-neutral-700 overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${
                    drawerAcceptPct === 100
                      ? 'bg-success-500'
                      : drawerAcceptPct >= 80
                        ? 'bg-warning-500'
                        : 'bg-danger-500'
                  }`}
                  style={{ width: `${Math.min(drawerAcceptPct, 100)}%` }}
                />
              </div>
            </div>

            {/* Per-line items */}
            <div className="space-y-2">
              {acceptedItems.map((ai, idx) => {
                const num = parseFloat(ai.accepted_quantity) || 0;
                const overAccept = num > ai.remaining_quantity;
                const shortReceipt = num < ai.remaining_quantity;
                return (
                  <div
                    key={ai.ref_id}
                    className={`flex items-center gap-3 p-3 rounded-xl ${
                      overAccept
                        ? 'bg-danger-50 dark:bg-danger-900/20'
                        : shortReceipt
                          ? 'bg-warning-50 dark:bg-warning-900/20'
                          : 'bg-neutral-50 dark:bg-neutral-800/50'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-neutral-800 dark:text-neutral-200 truncate">
                        {ai.product_name}
                      </p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 font-mono tabular-nums">
                        {ai.product_sku ?? (ai.item_type === 'INGREDIENT' ? 'Ingredient' : '')} · In transit: {ai.remaining_quantity}{ai.unit ? ` ${ai.unit}` : ''}
                      </p>
                    </div>
                    <div className="w-28 shrink-0">
                      <Input
                        type="number"
                        value={ai.accepted_quantity}
                        onChange={(v) => {
                          setAcceptedItems((prev) =>
                            prev.map((item, i) =>
                              i === idx ? { ...item, accepted_quantity: v } : item,
                            ),
                          );
                        }}
                        placeholder="0"
                        className={overAccept ? 'border-danger-400 focus:border-danger-500 focus:ring-danger-500' : ''}
                      />
                      {overAccept && (
                        <p className="text-[10px] text-danger-600 mt-0.5 leading-tight">
                          Exceeds qty in transit
                        </p>
                      )}
                    </div>
                    <div className="w-20 shrink-0 flex justify-end">
                      <VarianceBadge sent={ai.remaining_quantity} accepted={Math.max(0, num)} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Variance summary */}
            {drawerHasShortReceipt && !drawerOverAccepts && (
              <div className="rounded-xl bg-warning-50 dark:bg-warning-900/20 p-3 text-xs text-warning-800 dark:text-warning-300 leading-relaxed">
                <p>
                  Some lines are short. The rest stays in transit from <strong>{transfer.source_location_name}</strong> —
                  receive it when it arrives, or use <strong>Reject Remaining</strong> if it will not come.
                </p>
              </div>
            )}
            {drawerOverAccepts && (
              <div className="rounded-xl bg-danger-50 dark:bg-danger-900/20 p-3 text-xs text-danger-800 dark:text-danger-300 leading-relaxed">
                One or more lines accept more than was sent. Reduce the highlighted quantities before confirming.
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Notes (optional)
              </label>
              <textarea
                value={confirmNotes}
                onChange={(e) => setConfirmNotes(e.target.value)}
                placeholder="Add notes about the receipt..."
                rows={3}
                className="w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-3 py-2 text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-colors"
                maxLength={500}
              />
              <p className="text-xs text-neutral-400 mt-1 text-right tabular-nums font-mono">{confirmNotes.length}/500</p>
            </div>
          </div>

          {/* Sticky footer */}
          <div className="border-t border-neutral-200/70 dark:border-neutral-700/70 pt-4 pb-2 flex items-center justify-between gap-3">
            <Button variant="secondary" onClick={() => setShowReceiptDrawer(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={confirmMutation.isPending || drawerOverAccepts || drawerAccepted <= 0}
            >
              {confirmMutation.isPending ? 'Confirming...' : 'Confirm Receipt'}
            </Button>
          </div>
        </div>
      </Drawer>

      {/* Reject Remaining Modal — reason required; the rest is written off at the source (IS2-001) */}
      <Modal
        open={showRejectDialog}
        onClose={() => {
          setShowRejectDialog(false);
          setRejectReason('');
        }}
        title="Reject Remaining Stock"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setShowRejectDialog(false);
                setRejectReason('');
              }}
            >
              Go Back
            </Button>
            <Button
              variant="danger"
              onClick={handleRejectRemaining}
              disabled={rejectMutation.isPending || !rejectReason.trim()}
            >
              {rejectMutation.isPending ? 'Rejecting...' : 'Reject Remaining'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="rounded-xl bg-danger-50 dark:bg-danger-900/20 p-3 text-sm text-danger-800 dark:text-danger-300">
            The <strong className="font-mono tabular-nums">{totalInTransit}</strong> still in transit will be written off at{' '}
            <strong>{transfer.source_location_name}</strong> as damaged stock. It is not added back to any store. The transfer then closes.
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Reason (required)
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Spilled in transit"
              rows={3}
              aria-label="Reason for rejecting the remaining stock"
              className="w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-3 py-2 text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:border-danger-500 focus:ring-1 focus:ring-danger-500 outline-none transition-colors"
              maxLength={500}
            />
            <p className="text-xs text-neutral-400 mt-1 text-right tabular-nums font-mono">{rejectReason.length}/500</p>
          </div>
        </div>
      </Modal>

      {/* Cancel Transfer Modal — captures optional reason */}
      <Modal
        open={showCancelDialog}
        onClose={() => {
          setShowCancelDialog(false);
          setCancelReason('');
        }}
        title="Cancel Stock Transfer"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setShowCancelDialog(false);
                setCancelReason('');
              }}
            >
              Go Back
            </Button>
            <Button
              variant="danger"
              onClick={handleCancel}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Transfer'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="rounded-xl bg-warning-50 dark:bg-warning-900/20 p-3 text-sm text-warning-800 dark:text-warning-300">
            This will return all deducted stock to <strong>{transfer.source_location_name}</strong>. This action cannot be undone.
          </div>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Cancel <strong className="font-mono text-neutral-800 dark:text-neutral-200">{transfer.transfer_number}</strong> ({items.length} items).
          </p>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Reason (optional)
            </label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Explain why this transfer is being cancelled..."
              rows={3}
              className="w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-3 py-2 text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:border-danger-500 focus:ring-1 focus:ring-danger-500 outline-none transition-colors"
              maxLength={500}
            />
            <p className="text-xs text-neutral-400 mt-1 text-right tabular-nums font-mono">{cancelReason.length}/500</p>
          </div>
        </div>
      </Modal>
    </>
  );
}

// ─── Page export ────────────────────────────────────────────────────────────

export function StockTransferDetailPage() {
  const { id } = useParams<{ id: string }>();
  return (
    <PageContainer
      title="Transfer Detail"
      breadcrumbs={[
        { label: 'Inventory', href: '/inventory' },
        { label: 'Stock Transfers', href: '/inventory/stock-transfers' },
        { label: id?.slice(0, 8).toUpperCase() ?? '' },
      ]}
    >
      <ErrorBoundary>
        <StockTransferDetailContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
